/**
 * Collection Filters JavaScript
 * Handles AJAX filtering, sorting, and interactive features
 * Following THEME_GUIDELINES.md
 */

class CollectionFilters {
  constructor() {
    this.filterForm = document.querySelector('[data-filter-form]');
    this.sortSelect = document.querySelector('[data-sort-by]');
    this.productGrid = document.querySelector('[data-products-grid]');
    
    if (!this.filterForm && !this.sortSelect) return;
    
    this.init();
  }

  init() {
    // Handle filter changes
    if (this.filterForm) {
      // Prevent default form submission
      this.filterForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.onFilterChange();
      });
      
      // Handle filter changes
      this.filterForm.addEventListener('change', this.onFilterChange.bind(this));
      this.setupFilterGroups();
      this.setupActiveFilters();
    }

    // Handle sort changes
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', this.onSortChange.bind(this));
    }

    // Handle mobile filter toggle
    this.setupMobileFilters();
    
    // Handle mobile sticky bar
    this.setupMobileStickyBar();

    // Handle browser back/forward buttons
    window.addEventListener('popstate', () => {
      const searchParams = new URLSearchParams(window.location.search);
      this.renderPage(searchParams.toString(), null, false);
    });

    // Update selected counts
    this.updateSelectedCounts();
  }

  setupMobileFilters() {
    // Support both mobile bar buttons and regular filter toggles
    const toggleBtn = document.querySelector('[data-mobile-toggle-filters]') || document.querySelector('[data-toggle-filters]');
    const filterDrawer = document.querySelector('[data-filter-drawer]') || document.querySelector('.labratgifts-collection__sidebar');
    const closeBtn = document.querySelector('[data-close-filters]');
    
    if (!filterDrawer) return;
    
    const isDrawerLayout = filterDrawer.getAttribute('data-filter-layout') === 'drawer';
    
    // Store previousFocus on the drawer element to persist across reinitializations
    if (!filterDrawer._previousFocus) {
      filterDrawer._previousFocus = null;
    }
    
    // Create or reuse overlay
    let overlay = document.querySelector('.labratgifts-filter-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'labratgifts-filter-overlay';
      document.body.appendChild(overlay);
    }
    
    // Clean up old event listeners to prevent duplicates
    if (this._overlayClickHandler) {
      overlay.removeEventListener('click', this._overlayClickHandler);
    }
    if (this._escKeyHandler) {
      document.removeEventListener('keydown', this._escKeyHandler);
    }
    if (this._drawerTabHandler && filterDrawer) {
      filterDrawer.removeEventListener('keydown', this._drawerTabHandler);
    }
    if (this._toggleClickHandler && this._lastToggleBtn) {
      this._lastToggleBtn.removeEventListener('click', this._toggleClickHandler);
    }
    if (this._closeClickHandler && this._lastCloseBtn) {
      this._lastCloseBtn.removeEventListener('click', this._closeClickHandler);
    }
    
    // Open drawer
    const openDrawer = (triggerElement) => {
      filterDrawer.classList.add('labratgifts-collection__sidebar--open');
      if (isDrawerLayout) {
        filterDrawer.setAttribute('aria-hidden', 'false');
      }
      document.body.classList.add('labratgifts-filters-open');
      
      // Focus management for drawer mode
      if (isDrawerLayout) {
        filterDrawer._previousFocus = triggerElement || document.activeElement;
        // Move focus to close button or first focusable element
        const focusTarget = closeBtn || filterDrawer.querySelector('a, button, input, select');
        if (focusTarget) {
          setTimeout(() => focusTarget.focus(), 50);
        }
      }
    };
    
    // Close drawer
    const closeDrawer = () => {
      filterDrawer.classList.remove('labratgifts-collection__sidebar--open');
      if (isDrawerLayout) {
        filterDrawer.setAttribute('aria-hidden', 'true');
      }
      document.body.classList.remove('labratgifts-filters-open');
      
      // Remove active state from mobile filter button
      const mobileFilterBtn = document.querySelector('[data-mobile-toggle-filters]');
      if (mobileFilterBtn) {
        mobileFilterBtn.classList.remove('active');
      }
      
      // Return focus to trigger for drawer mode
      if (isDrawerLayout && filterDrawer._previousFocus) {
        filterDrawer._previousFocus.focus();
        filterDrawer._previousFocus = null;
      }
    };
    
    // Focus trap for drawer mode
    if (isDrawerLayout) {
      this._drawerTabHandler = (e) => {
        if (e.key === 'Tab' && filterDrawer.classList.contains('labratgifts-collection__sidebar--open')) {
          const focusableElements = filterDrawer.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusable = focusableElements[0];
          const lastFocusable = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey && document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          } else if (!e.shiftKey && document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      };
      filterDrawer.addEventListener('keydown', this._drawerTabHandler);
    }
    
    // Toolbar toggle button (drawer layout desktop) - store reference for cleanup
    if (toggleBtn) {
      this._toggleClickHandler = () => openDrawer(toggleBtn);
      this._lastToggleBtn = toggleBtn;
      toggleBtn.addEventListener('click', this._toggleClickHandler);
    }
    
    // Close button - store reference for cleanup
    if (closeBtn) {
      this._closeClickHandler = closeDrawer;
      this._lastCloseBtn = closeBtn;
      closeBtn.addEventListener('click', this._closeClickHandler);
    }
    
    // Overlay click - store reference for cleanup
    this._overlayClickHandler = closeDrawer;
    overlay.addEventListener('click', this._overlayClickHandler);
    
    // ESC key to close - store reference for cleanup
    this._escKeyHandler = (e) => {
      if (e.key === 'Escape' && filterDrawer.classList.contains('labratgifts-collection__sidebar--open')) {
        closeDrawer();
      }
    };
    document.addEventListener('keydown', this._escKeyHandler);
    
    // Expose methods for mobile sticky bar
    window.labratgiftsFilterDrawer = {
      open: (trigger) => openDrawer(trigger),
      close: closeDrawer
    };
    
    // Disconnect previous overlay observer if it exists (prevent memory leaks)
    if (this._overlayObserver) {
      this._overlayObserver.disconnect();
    }
    
    // Ensure overlay cleanup when drawer closes via MutationObserver
    this._overlayObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const hasFilterClass = document.body.classList.contains('labratgifts-filters-open');
          if (!hasFilterClass && overlay) {
            // Remove any inline styles that might persist
            overlay.style.opacity = '';
            overlay.style.visibility = '';
          }
        }
      });
    });
    this._overlayObserver.observe(document.body, { attributes: true });
  }

  setupMobileStickyBar() {
    const filterBtn = document.querySelector('[data-mobile-toggle-filters]');
    const sortBtn = document.querySelector('[data-mobile-toggle-sort]');
    const sortSelect = document.querySelector('#SortBy') || document.querySelector('[data-sort-by]');
    
    // Mobile filter button - use the openDrawer function from setupMobileFilters
    if (filterBtn) {
      filterBtn.addEventListener('click', () => {
        // Use the drawer API if available
        if (window.labratgiftsFilterDrawer) {
          window.labratgiftsFilterDrawer.open(filterBtn);
          filterBtn.classList.add('active');
        } else {
          // Fallback: trigger existing open logic by adding class
          const drawer = document.querySelector('[data-filter-drawer]') || document.querySelector('.labratgifts-collection__sidebar');
          if (drawer) {
            drawer.classList.add('labratgifts-collection__sidebar--open');
            document.body.classList.add('labratgifts-filters-open');
            filterBtn.classList.add('active');
          }
        }
      });
    }
    
    // Mobile sort button - create mobile sort drawer
    if (sortBtn && sortSelect) {
      sortBtn.addEventListener('click', () => {
        // Remove existing drawer if any
        const existingDrawer = document.querySelector('.labratgifts-mobile-sort-drawer');
        if (existingDrawer) {
          existingDrawer.remove();
        }
        
        // Create mobile sort drawer
        const sortDrawer = document.createElement('div');
        sortDrawer.className = 'labratgifts-mobile-sort-drawer labratgifts-mobile-sort-drawer--open';
        sortDrawer.innerHTML = `
          <div class="labratgifts-mobile-sort-drawer__header">
            <h3>Sort By</h3>
            <button type="button" class="labratgifts-mobile-sort-drawer__close" aria-label="Close">×</button>
          </div>
          <div class="labratgifts-mobile-sort-drawer__options">
            ${Array.from(sortSelect.options).map(option => `
              <button type="button" 
                      class="labratgifts-mobile-sort-option ${option.selected ? 'active' : ''}" 
                      data-sort-value="${option.value}">
                ${option.text}
              </button>
            `).join('')}
          </div>
        `;
        document.body.appendChild(sortDrawer);
        
        // Handle option clicks
        sortDrawer.querySelectorAll('.labratgifts-mobile-sort-option').forEach(btn => {
          btn.addEventListener('click', () => {
            sortSelect.value = btn.dataset.sortValue;
            sortSelect.dispatchEvent(new Event('change'));
            sortDrawer.remove();
          });
        });
        
        // Handle close button
        const closeBtn = sortDrawer.querySelector('.labratgifts-mobile-sort-drawer__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            sortDrawer.remove();
          });
        }
        
        // Close on background click
        sortDrawer.addEventListener('click', (e) => {
          if (e.target === sortDrawer) {
            sortDrawer.remove();
          }
        });
      });
    }
  }

  setupFilterGroups() {
    // Handle filter group resets
    const groupResets = this.filterForm.querySelectorAll('[data-group-reset]');
    groupResets.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const details = btn.closest('.labratgifts-filter-group__details');
        const checkboxes = details.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(cb => cb.checked = false);
        this.onFilterChange();
      });
    });

    // Handle details toggle
    const details = this.filterForm.querySelectorAll('.labratgifts-filter-group__details');
    details.forEach(detail => {
      detail.addEventListener('toggle', () => {
        const icon = detail.querySelector('.labratgifts-icon--sm');
        if (icon) {
          icon.style.transform = detail.open ? 'rotate(180deg)' : 'rotate(0deg)';
        }
      });
    });
  }

  setupActiveFilters() {
    const activeFilters = document.querySelectorAll('.labratgifts-active-filter');
    activeFilters.forEach(filter => {
      filter.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(filter.href);
        this.renderPage(url.search.substring(1), null, true);
      });
    });
    
    // Handle clear all
    const clearAll = document.querySelector('.labratgifts-filters__clear-all');
    if (clearAll) {
      clearAll.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(clearAll.href);
        this.renderPage(url.search.substring(1), null, true);
      });
    }
  }

  onFilterChange(e) {
    const formData = new FormData(this.filterForm);
    const searchParams = new URLSearchParams(formData);
    
    // Update URL and filters with AJAX
    this.renderPage(searchParams.toString(), null, true);
  }

  onSortChange(e) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('sort_by', e.target.value);
    
    // Update URL and products with AJAX
    this.renderPage(searchParams.toString(), null, true);
  }

  renderPage(searchParams, event, updateURLHash = true) {
    const url = `${window.location.pathname}?${searchParams}`;
    const sectionUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=main-collection`;
    
    // Show loading state
    if (this.productGrid) {
      this.productGrid.classList.add('loading');
    }
    
    // Fetch updated content
    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newProductGrid = html.querySelector('[data-products-grid]');
        const newFilters = html.querySelector('[data-filter-form]');
        
        // Update product grid
        if (newProductGrid && this.productGrid) {
          this.productGrid.innerHTML = newProductGrid.innerHTML;
        }
        
        // Update filters
        if (newFilters && this.filterForm) {
          const currentFilterContent = this.filterForm.innerHTML;
          this.filterForm.innerHTML = newFilters.innerHTML;
          this.setupFilterGroups();
          this.updateSelectedCounts();
          // Re-bind mobile filter handlers after AJAX refresh
          this.setupMobileFilters();
        }
        
        // Update URL
        if (updateURLHash) {
          window.history.pushState({}, '', url);
        }
        
        // Remove loading state
        if (this.productGrid) {
          this.productGrid.classList.remove('loading');
        }
        
        // Update active filters
        this.setupActiveFilters();
      })
      .catch(error => {
        console.error('Filter error:', error);
        // Fallback to page reload on error
        window.location.href = url;
      });
  }

  updateSelectedCounts() {
    const filterGroups = document.querySelectorAll('.labratgifts-filter-group');
    
    filterGroups.forEach(group => {
      const checkboxes = group.querySelectorAll('input[type="checkbox"]:checked');
      const countDisplay = group.querySelector('.labratgifts-filter-selected-count');
      
      if (countDisplay) {
        countDisplay.textContent = `${checkboxes.length} selected`;
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionFilters();
  });
} else {
  new CollectionFilters();
}
